import { api } from "@/api";
import { MutationConfig, MutationResult } from "@/shared/types/base";
import { notify } from "@/shared/notification";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import { KudeTicketDatosResponse } from "@/features/inventario/ventas/api/get-venta-kude-ticket-datos";

// Enviar DE a SIFEN
export async function enviarDE(id: string): Promise<any> {
    const response = await api.post(`de/${id}/enviar`);
    return response.data;
}

export function useEnviarDEMutation(mutationConfig: MutationConfig<any> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => await enviarDE(id),
        onSuccess: (data, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, id, ...args);
            notify("DE enviado a SIFEN correctamente", { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["de", id] });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al enviar el DE a SIFEN", { variant: "error" });
        },
    });
}

export interface EnviarDEMutationProps {
    mutationConfig?: MutationConfig<any>,
    children?: (mutationResult: MutationResult<any>) => ReactNode
}

export function EnviarDEMutation({
    children,
    mutationConfig
}: EnviarDEMutationProps) {
    const mutationResult = useEnviarDEMutation(mutationConfig);
    return children && children(mutationResult);
}

// Consultar Lote
export async function consultarLote(id: string): Promise<any> {
    const response = await api.post(`de/${id}/consultar-lote`);
    return response.data;
}

export function useConsultarLoteMutation(mutationConfig: MutationConfig<any> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => await consultarLote(id),
        onSuccess: (data, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, id, ...args);
            notify("Consulta de lote realizada correctamente", { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["de", id] });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al consultar el lote", { variant: "error" });
        },
    });
}

export interface ConsultarLoteMutationProps {
    mutationConfig?: MutationConfig<any>,
    children?: (mutationResult: MutationResult<any>) => ReactNode
}

export function ConsultarLoteMutation({
    children,
    mutationConfig
}: ConsultarLoteMutationProps) {
    const mutationResult = useConsultarLoteMutation(mutationConfig);
    return children && children(mutationResult);
}

// Consultar DE (consulta individual por CDC)
export async function consultarDE(id: string): Promise<any> {
    const response = await api.post(`de/${id}/consultar-de`);
    return response.data;
}

// Color de la notificación según el código de resultado devuelto por SIFEN (0420/0421/0422/otro)
function getVariantForCodigoConsultaDE(codigo?: string): "success" | "warning" | "info" {
    switch (codigo) {
        case "0422": return "success"; // CDC encontrado / aprobado
        case "0420": return "warning"; // CDC inexistente
        default: return "info"; // código no reconocido
    }
}

export function useConsultarDEMutation(mutationConfig: MutationConfig<any> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => await consultarDE(id),
        onSuccess: (data, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, id, ...args);
            notify(data?.message || "Consulta de DE realizada correctamente", { variant: getVariantForCodigoConsultaDE(data?.codigo) });
            queryClient.invalidateQueries({ queryKey: ["de", id] });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al consultar el DE", { variant: "error" });
        },
    });
}

export interface ConsultarDEMutationProps {
    mutationConfig?: MutationConfig<any>,
    children?: (mutationResult: MutationResult<any>) => ReactNode
}

export function ConsultarDEMutation({
    children,
    mutationConfig
}: ConsultarDEMutationProps) {
    const mutationResult = useConsultarDEMutation(mutationConfig);
    return children && children(mutationResult);
}

// Descargar XML
export async function downloadXml(id: string): Promise<Blob> {
    const response = await api.get(`de/${id}/xml/download`, {
        responseType: "blob",
    });
    return response.data;
}

export function useDownloadXmlMutation(mutationConfig: MutationConfig<Blob> = {}) {
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => {
            notify("Descargando XML...", { variant: "info" });
            return await downloadXml(id);
        },
        onSuccess: (blob, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(blob, id, ...args);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `DE_${id}.xml`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            notify("XML descargado correctamente", { variant: "success" });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            notify("Error al descargar el XML", { variant: "error" });
        },
    });
}

export interface DownloadXmlMutationProps {
    mutationConfig?: MutationConfig<Blob>,
    children?: (mutationResult: MutationResult<Blob>) => ReactNode
}

export function DownloadXmlMutation({
    children,
    mutationConfig
}: DownloadXmlMutationProps) {
    const mutationResult = useDownloadXmlMutation(mutationConfig);
    return children && children(mutationResult);
}

// Descargar KuDE
export async function downloadKude(id: string): Promise<Blob> {
    const response = await api.get(`de/kude/${id}`, {
        responseType: "blob",
    });
    return response.data;
}

export function useDownloadKudeMutation(mutationConfig: MutationConfig<Blob> = {}) {
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => {
            notify("Descargando KuDE...", { variant: "info" });
            return await downloadKude(id);
        },
        onSuccess: (blob, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(blob, id, ...args);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `KuDE_${id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            notify("KuDE descargado correctamente", { variant: "success" });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            notify("Error al descargar el KuDE", { variant: "error" });
        },
    });
}

export interface DownloadKudeMutationProps {
    mutationConfig?: MutationConfig<Blob>,
    children?: (mutationResult: MutationResult<Blob>) => ReactNode
}

export function DownloadKudeMutation({
    children,
    mutationConfig
}: DownloadKudeMutationProps) {
    const mutationResult = useDownloadKudeMutation(mutationConfig);
    return children && children(mutationResult);
}

// Obtener datos del KuDE en modo Ticket como JSON (para impresión HTML)
export async function getKudeTicketDatos(id: string): Promise<KudeTicketDatosResponse> {
    const response = await api.get<KudeTicketDatosResponse>(`de/kude-ticket-datos/${id}`);
    return response.data;
}

// Reservar numeración DE
export interface ReservarDERequest {
    tipoDocumento: number;
    establecimiento: string;
    puntoExpedicion: string;
}

export interface ReservarDEResponse {
    id: string;
    numeroDocumento?: number;
    fechaEmision?: string;
}

export async function reservarDE(request: ReservarDERequest): Promise<ReservarDEResponse> {
    const response = await api.post("de/reservar", request);
    return response.data;
}

export function useReservarDEMutation(mutationConfig: MutationConfig<ReservarDEResponse> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (request: ReservarDERequest) => await reservarDE(request),
        onSuccess: (data, variables, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, variables, ...args);
            notify(`Numeración ${data.numeroDocumento} reservada correctamente`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al reservar la numeración", { variant: "error" });
        },
    });
}

// Eliminar DE reservado
export async function eliminarDEReservado(id: string): Promise<any> {
    const response = await api.delete(`de/${id}/reservado`);
    return response.data;
}

export function useEliminarDEReservadoMutation(mutationConfig: MutationConfig<any> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => await eliminarDEReservado(id),
        onSuccess: (data, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, id, ...args);
            notify("Numeración reservada inutilizada correctamente", { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["de", id] });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al eliminar la numeración reservada", { variant: "error" });
        },
    });
}

// Reenviar Email
export async function reenviarEmail(id: string): Promise<any> {
    const response = await api.post(`emails/reenviar/${id}`);
    return response.data;
}

export function useReenviarEmailMutation(mutationConfig: MutationConfig<any> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: async (id: string) => await reenviarEmail(id),
        onSuccess: (data, id, ...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(data, id, ...args);
            notify("Email reenviado correctamente", { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["de", id] });
            queryClient.invalidateQueries({ queryKey: ["documentos-electronicos"] });
        },
        onError: (...args) => {
            mutationConfig.onError && mutationConfig.onError(...args);
            const error = args[0] as any;
            notify(error.response?.data?.message || "Error al reenviar el email", { variant: "error" });
        },
    });
}

export interface ReenviarEmailMutationProps {
    mutationConfig?: MutationConfig<any>,
    children?: (mutationResult: MutationResult<any>) => ReactNode
}

export function ReenviarEmailMutation({
    children,
    mutationConfig
}: ReenviarEmailMutationProps) {
    const mutationResult = useReenviarEmailMutation(mutationConfig);
    return children && children(mutationResult);
}
